import {
  Input,
  InputGroup,
  InputLeftAddon,
  InputProps,
  Text,
  TextProps,
} from '@chakra-ui/react';
import { Currency, Dinero, dinero, equal, toFormat, toUnit } from 'dinero.js';
import * as React from 'react';

import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';

const dineroFromFloat = ({
  amount,
  currency,
  scale,
}: {
  amount: number;
  currency: Currency<number>;
  scale: number;
}) => {
  return dinero({
    amount: Math.round(amount * (currency.base ** currency.exponent || scale)),
    currency,
    scale,
  });
};

export const currencyDisplay = (
  value: Dinero<number>,
  options?: Intl.NumberFormatOptions
) => {
  return toFormat(value, ({ amount, currency }) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.code,
      ...options,
    }).format(amount)
  );
};

export const CurrencyDisplay: React.FC<
  {
    value: Dinero<number>;
    options?: Intl.NumberFormatOptions;
  } & TextProps
> = ({ value, options, ...props }) => {
  return (
    <Text as="span" {...props}>
      {currencyDisplay(value, options)}
    </Text>
  );
};

// this only works right now for CAD and USD. As soon as we add
// something else we will need to come up with a better solution, but at least
// the API for this component should be solid
export const CurrencyInput = React.forwardRef<
  HTMLInputElement,
  {
    value?: Dinero<number>;
    onChange?: (value?: Dinero<number>) => void;
  } & Omit<InputProps, 'value' | 'onChange'>
>(({ value, onChange = () => {}, ...props }, ref) => {
  const { currency } = useDefaultCurrency();

  const [internalValue, setInternalValue] = React.useState(
    value ? toUnit(value).toFixed(2) : ''
  );

  const currentValue = dineroFromFloat({
    amount: Number(internalValue),
    currency,
    scale: 2,
  });

  React.useEffect(() => {
    if (!value) {
      setInternalValue('');
    }
  }, [value]);

  React.useEffect(() => {
    if (value && !equal(currentValue, value)) {
      setInternalValue(toUnit(value).toFixed(2));
    }
  }, [currentValue, value]);

  return (
    <InputGroup>
      <InputLeftAddon>$</InputLeftAddon>
      <Input
        ref={ref}
        {...props}
        value={internalValue}
        type="number"
        onWheel={(e) => e.currentTarget.blur()}
        onChange={(event) => {
          const newValue = dineroFromFloat({
            amount: Number(event.target.value),
            currency,
            scale: 2,
          });
          onChange(newValue);
          setInternalValue(event.target.value);
        }}
      />
    </InputGroup>
  );
});
CurrencyInput.displayName = 'CurrencyInput';
